export enum NewRelicLogLevel {
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
  DEBUG = 'DEBUG',
  TRACE = 'TRACE',
}

declare global {
  interface Window {
    newrelic?: {
      log: (message: string, options?: { customAttributes?: Record<string, any>; level?: string }) => void;
      setCustomAttribute: (name: string, value: string | number | boolean) => void;
      noticeError: (error: Error | string, customAttributes?: Record<string, any>) => void;
      addPageAction: (name: string, attributes?: Record<string, any>) => void;
      setCurrentRouteName: (name: string) => void;
    };
  }
}

export function setNewRelicCustomAttribute(name: string, value: string | number | boolean) {
  if (!window.newrelic) return;
  window.newrelic.setCustomAttribute(name, value);
}

export function newRelicLog(
  message: string,
  attributes: Record<string, any> = {},
  level: NewRelicLogLevel = NewRelicLogLevel.INFO,
) {
  if (!window.newrelic) return;
  window.newrelic.log(message, { customAttributes: attributes, level });
}

export function newRelicNoticeError(error: Error | string, customAttributes: Record<string, any> = {}) {
  if (!window.newrelic) return;
  window.newrelic.noticeError(error, customAttributes);
}

export function newRelicAddPageAction(name: string, attributes: Record<string, any> = {}) {
  if (!window.newrelic) return;
  window.newrelic.addPageAction(name, attributes);
}

/* export function setNewRelicRouteName(name: string) {
  if (!window.newrelic) return;
  window.newrelic.setCurrentRouteName(name);
} */

/**
 * Installs a global unhandledrejection listener that sends rejected promises
 * (e.g. "Failed to fetch") to New Relic with contextual attributes so the
 * source of the error can be identified in the NR dashboard.
 */
export function registerUnhandledRejectionHandler() {
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (!window.newrelic) return;

    const reason = event.reason;
    const error: Error =
      reason instanceof Error ? reason : new Error(String(reason ?? 'Unknown rejection'));

    const params = new URLSearchParams(window.location.search);

    const attributes: Record<string, string> = {
      url: window.location.href,
      path: window.location.pathname,
      action: 'unhandledrejection',
    };

    const formId = params.get('formId');
    if (formId) attributes.formId = formId;

    const taskId = params.get('taskId');
    if (taskId) attributes.taskId = taskId;

    window.newrelic.noticeError(error, attributes);
  });
}
